import type { ValidationResult } from "@led-signage/shared/common";
import type {
  DisplayDetail,
  PlaylistRevision,
  Schedule,
  ScheduleRevision,
} from "@led-signage/shared/management-api";
import { useEffect, useState, type ReactNode } from "react";
import { newIdempotencyKey } from "./api/client.js";
import { describeFailure, type ActionFailure } from "./api/errors.js";
import { AuthenticationRecoveryButton } from "./AuthenticationRecoveryButton.js";
import { api, usingFixtures } from "./api/index.js";
import { useResource, type Resource } from "./api/useResource.js";
import {
  ALL_DAYS,
  PRIORITY_BANDS,
  WEEKDAY_LABELS,
  WEEKDAYS,
  WEEKEND,
  applyPurpose,
  describeDays,
  emptyForm,
  hasBlockingIssue,
  hasDay,
  inspect,
  toInputTime,
  toRequest,
  toggleDay,
  type FormIssue,
  type ScheduleFormState,
  type SchedulePurpose,
} from "./schedule-editor.js";

function Gate<T>({
  resource,
  children,
}: {
  resource: Resource<T>;
  children: (value: T) => ReactNode;
}) {
  if (resource.state === "loading") {
    return <p className="resource-note">読み込み中…</p>;
  }
  if (resource.state === "error") {
    return (
      <div className="inline-alert">
        <b>読み込めませんでした</b>
        <span>{resource.message}</span>
      </div>
    );
  }
  return <>{children(resource.value)}</>;
}

type ActionState =
  | { kind: "idle" }
  | { kind: "running"; label: string }
  | { kind: "success"; message: string }
  | { kind: "failed"; failure: ActionFailure };

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function fromRevision(revision: ScheduleRevision): ScheduleFormState {
  const band =
    PRIORITY_BANDS.find(
      (item) =>
        revision.priority >= item.minimum && revision.priority <= item.maximum,
    ) ?? PRIORITY_BANDS[0];
  return {
    purpose: band?.purpose ?? "normal",
    playlistRevisionId: revision.playlistRevisionId,
    validFrom: revision.validFrom,
    validUntil: revision.validUntil ?? "",
    startTime: toInputTime(revision.startTime),
    endTime: toInputTime(revision.endTime),
    daysMask: revision.daysMask,
    priority: revision.priority,
    transitionMode: revision.transitionMode,
    maxWaitSeconds:
      revision.maxWaitSeconds === null ? "" : String(revision.maxWaitSeconds),
    displayIds: revision.displayIds,
  };
}

function IssueList({ issues }: { issues: readonly FormIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul className="form-issues">
      {issues.map((issue) => (
        <li
          key={`${issue.field}-${issue.message}`}
          className={`form-issue form-issue--${issue.severity}`}
        >
          {issue.message}
        </li>
      ))}
    </ul>
  );
}

function ValidationSummary({
  result,
  conflicts,
}: {
  result: ValidationResult | null;
  conflicts: readonly string[];
}) {
  if (result === null) return null;
  return (
    <div className={`inline-alert inline-alert--${result.valid ? "ok" : "error"}`}>
      <b>{result.valid ? "公開前検査に通りました" : "公開前検査で問題が見つかりました"}</b>
      {result.errors.map((issue) => (
        <span key={`e-${issue.code}-${issue.message}`}>
          エラー: {issue.message}
        </span>
      ))}
      {result.warnings.map((issue) => (
        <span key={`w-${issue.code}-${issue.message}`}>
          警告: {issue.message}
        </span>
      ))}
      {conflicts.length > 0 && (
        <span>
          同一Displayで競合しているRevision: {conflicts.join(", ")}
        </span>
      )}
    </div>
  );
}

export function ScheduleSection() {
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const schedules = useResource(
    (signal) => api.listSchedules(undefined, signal),
    [reloadToken],
  );
  const detail = useResource(
    (signal) =>
      selectedId === null
        ? Promise.resolve(null)
        : api.getSchedule(selectedId, signal),
    [selectedId, reloadToken],
  );
  const displays = useResource((signal) => api.listDisplays(signal), []);
  const playlists = useResource((signal) => api.listPlaylists(signal), []);

  const [revision, setRevision] = useState<ScheduleRevision | null>(null);
  const [form, setForm] = useState<ScheduleFormState>(() => emptyForm(today()));
  const [action, setAction] = useState<ActionState>({ kind: "idle" });
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [conflicts, setConflicts] = useState<readonly string[]>([]);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (detail.state !== "ready" || detail.value === null) return;
    const latest = detail.value.latestRevision;
    setRevision(latest);
    setForm(latest === null ? emptyForm(today()) : fromRevision(latest));
    setValidation(null);
    setConflicts([]);
  }, [detail.state, detail.state === "ready" ? detail.value : null]);

  const issues = inspect(form);
  const blocked = hasBlockingIssue(issues);
  const busy = action.kind === "running";
  const requestBlockedBy =
    revision === null
      ? "編成が読み込まれていません。"
      : blocked
        ? "上のエラーを解消してください。"
        : reason.trim().length === 0
          ? "変更理由を入力してください。"
          : null;
  // 公開済みRevisionは変更できない。仕様上draftだけが編集可能。
  const editable = revision === null || revision.state === "draft";

  async function save() {
    if (revision === null) return;
    setAction({ kind: "running", label: "保存中…" });
    try {
      const saved = await api.updateScheduleRevision(
        revision.id,
        toRequest(form),
        revision.etag,
      );
      setRevision(saved);
      setAction({ kind: "success", message: "保存しました。" });
      setValidation(null);
      setConflicts([]);
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  async function runValidation() {
    if (revision === null) return;
    setAction({ kind: "running", label: "検査中…" });
    try {
      const [result, current] = await Promise.all([
        api.validateScheduleRevision(revision.id),
        api.getScheduleRevision(revision.id),
      ]);
      setValidation(result);
      setConflicts(current.conflictingRevisionIds);
      setAction({ kind: "idle" });
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  async function requestPublication() {
    if (revision === null) return;
    if (reason.trim().length === 0) return;
    setAction({ kind: "running", label: "申請中…" });
    try {
      const created = await api.createPublicationRequest(
        {
          scheduleRevisionIds: [revision.id],
          displayIds: form.displayIds,
          changeReason: reason.trim(),
          sameDayChange: form.validFrom === today(),
          emergencyOverride: false,
          emergencyEndsAt: null,
        },
        newIdempotencyKey(),
      );
      await api.submitPublicationRequest(created.id, newIdempotencyKey());
      setAction({
        kind: "success",
        message: "承認を依頼しました。承認待ち画面から承認できます。",
      });
      setReason("");
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  async function createDraft() {
    if (selectedId === null) return;
    setAction({ kind: "running", label: "作成中…" });
    try {
      await api.createScheduleRevision(
        selectedId,
        toRequest(form),
        newIdempotencyKey(),
      );
      setAction({ kind: "success", message: "新しいdraftを作成しました。" });
      setReloadToken((token) => token + 1);
    } catch (error) {
      setAction({ kind: "failed", failure: describeFailure(error) });
    }
  }

  return (
    <>
      <SectionHeading />
      {usingFixtures && (
        <div className="resource-note resource-note--fixture">
          表示中のデータはローカル確認用のサンプルです。保存や検査は実APIでのみ実行できます。
        </div>
      )}

      <div className="editor-layout">
        <section className="panel">
          <h3>スケジュール</h3>
          <Gate resource={schedules}>
            {(page) =>
              page.items.length === 0 ? (
                <p className="resource-note">スケジュールがまだありません。</p>
              ) : (
                <ul className="resource-list">
                  {page.items.map((schedule: Schedule) => (
                    <li key={schedule.id}>
                      <button
                        type="button"
                        className={schedule.id === selectedId ? "active" : ""}
                        onClick={() => {
                          setSelectedId(schedule.id);
                          setAction({ kind: "idle" });
                        }}
                      >
                        {schedule.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            }
          </Gate>
        </section>

        <section className="panel">
          {selectedId === null ? (
            <p className="resource-note">左からスケジュールを選んでください。</p>
          ) : (
            <>
              <h3>
                編成の設定
                {revision !== null && (
                  <span className="revision-badge">
                    Revision {revision.revisionNumber}・{revision.state}
                  </span>
                )}
              </h3>

              {!editable && (
                <div className="inline-alert">
                  <b>このRevisionは編集できません</b>
                  <span>
                    公開済みのRevisionは変更しません。内容を変えるには新しいdraftを作成してください。
                  </span>
                </div>
              )}

              <label className="field">
                <span>用途</span>
                <select
                  value={form.purpose}
                  disabled={!editable}
                  onChange={(event) => {
                    setForm((current) =>
                      applyPurpose(
                        current,
                        event.target.value as SchedulePurpose,
                      ),
                    );
                  }}
                >
                  {PRIORITY_BANDS.map((band) => (
                    <option key={band.purpose} value={band.purpose}>
                      {band.label}（{band.minimum}〜{band.maximum}）— {band.hint}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>優先度</span>
                <input
                  type="number"
                  min={0}
                  max={1000}
                  value={form.priority}
                  disabled={!editable}
                  onChange={(event) => {
                    setForm((current) => ({
                      ...current,
                      priority: Number(event.target.value),
                    }));
                  }}
                />
              </label>

              <label className="field">
                <span>プレイリスト</span>
                <Gate resource={playlists}>
                  {(page) => (
                    <select
                      value={form.playlistRevisionId}
                      disabled={!editable}
                      onChange={(event) => {
                        setForm((current) => ({
                          ...current,
                          playlistRevisionId: event.target.value,
                        }));
                      }}
                    >
                      <option value="">選択してください</option>
                      {page.items.map((playlist) => (
                        <PlaylistOptions
                          key={playlist.id}
                          playlistId={playlist.id}
                          name={playlist.name}
                        />
                      ))}
                    </select>
                  )}
                </Gate>
              </label>

              <div className="field-row">
                <label className="field">
                  <span>開始日</span>
                  <input
                    type="date"
                    value={form.validFrom}
                    disabled={!editable}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        validFrom: event.target.value,
                      }));
                    }}
                  />
                </label>
                <label className="field">
                  <span>終了日（空で無期限）</span>
                  <input
                    type="date"
                    value={form.validUntil}
                    disabled={!editable}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        validUntil: event.target.value,
                      }));
                    }}
                  />
                </label>
              </div>

              <div className="field-row">
                <label className="field">
                  <span>開始時刻</span>
                  <input
                    type="time"
                    value={form.startTime}
                    disabled={!editable}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        startTime: event.target.value,
                      }));
                    }}
                  />
                </label>
                <label className="field">
                  <span>終了時刻</span>
                  <input
                    type="time"
                    value={form.endTime}
                    disabled={!editable}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        endTime: event.target.value,
                      }));
                    }}
                  />
                </label>
              </div>

              {form.startTime > form.endTime && (
                <p className="resource-note">
                  終了時刻が開始時刻より前のため、日をまたぐ編成として登録します。
                </p>
              )}

              <div className="field">
                <span>曜日（{describeDays(form.daysMask)}）</span>
                <div className="weekday-picker">
                  {WEEKDAY_LABELS.map((label, index) => (
                    <button
                      key={label}
                      type="button"
                      disabled={!editable}
                      className={hasDay(form.daysMask, index) ? "active" : ""}
                      aria-pressed={hasDay(form.daysMask, index)}
                      onClick={() => {
                        setForm((current) => ({
                          ...current,
                          daysMask: toggleDay(current.daysMask, index),
                        }));
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="weekday-presets">
                  {(
                    [
                      ["毎日", ALL_DAYS],
                      ["平日", WEEKDAYS],
                      ["土日", WEEKEND],
                    ] as const
                  ).map(([label, mask]) => (
                    <button
                      key={label}
                      type="button"
                      disabled={!editable}
                      onClick={() => {
                        setForm((current) => ({ ...current, daysMask: mask }));
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field">
                <span>対象のLED</span>
                <Gate resource={displays}>
                  {(page) => (
                    <div className="display-picker">
                      {page.items.map((item: DisplayDetail) => (
                        <label key={item.display.id}>
                          <input
                            type="checkbox"
                            disabled={!editable}
                            checked={form.displayIds.includes(item.display.id)}
                            onChange={() => {
                              setForm((current) => ({
                                ...current,
                                displayIds: current.displayIds.includes(
                                  item.display.id,
                                )
                                  ? current.displayIds.filter(
                                      (id) => id !== item.display.id,
                                    )
                                  : [...current.displayIds, item.display.id],
                              }));
                            }}
                          />
                          {item.display.name}
                        </label>
                      ))}
                    </div>
                  )}
                </Gate>
              </div>

              <div className="field-row">
                <label className="field">
                  <span>切替方式</span>
                  <select
                    value={form.transitionMode}
                    disabled={!editable}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        transitionMode: event.target
                          .value as ScheduleFormState["transitionMode"],
                      }));
                    }}
                  >
                    <option value="finish_current">再生中の動画を終えてから</option>
                    <option value="at_time">指定時刻ちょうどに</option>
                    <option value="immediate">即時</option>
                  </select>
                </label>
                <label className="field">
                  <span>最大待ち時間（秒・空で無制限）</span>
                  <input
                    type="number"
                    min={0}
                    max={86400}
                    value={form.maxWaitSeconds}
                    disabled={!editable}
                    onChange={(event) => {
                      setForm((current) => ({
                        ...current,
                        maxWaitSeconds: event.target.value,
                      }));
                    }}
                  />
                </label>
              </div>

              <IssueList issues={issues} />
              <ValidationSummary result={validation} conflicts={conflicts} />

              {action.kind === "failed" && (
                <div className="inline-alert inline-alert--error">
                  <b>実行できませんでした</b>
                  <span>{action.failure.message}</span>
                  <AuthenticationRecoveryButton failure={action.failure} />
                  {action.failure.reloadHelps && (
                    <button
                      type="button"
                      onClick={() => {
                        setReloadToken((token) => token + 1);
                      }}
                    >
                      読み込み直す
                    </button>
                  )}
                </div>
              )}
              {action.kind === "success" && (
                <p className="resource-note">{action.message}</p>
              )}

              {editable && (
                <>
              <label className="field">
                <span>変更理由（承認依頼に必須。監査ログへ記録されます）</span>
                <input
                  type="text"
                  value={reason}
                  placeholder="例: 9月のキャンペーン開始"
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                />
              </label>

              {form.validFrom === today() && (
                <div className="inline-alert inline-alert--error">
                  <b>当日変更です</b>
                  <span>
                    開始日が本日のため、当日変更として申請します。通常より強い確認が求められます。
                  </span>
                </div>
              )}

                </>
              )}

              <div className="editor-actions">
                {editable ? (
                  <>
                    <button
                      type="button"
                      disabled={blocked || busy || revision === null}
                      onClick={() => void save()}
                    >
                      {busy ? action.label : "保存"}
                    </button>
                    <button
                      type="button"
                      disabled={busy || revision === null}
                      onClick={() => void runValidation()}
                    >
                      公開前検査
                    </button>
                    <button
                      type="button"
                      disabled={requestBlockedBy !== null || busy}
                      onClick={() => void requestPublication()}
                    >
                      承認を依頼する
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={blocked || busy}
                    onClick={() => void createDraft()}
                  >
                    新しいdraftを作成して編集する
                  </button>
                )}
              </div>

              {/* 押せないボタンを黙って灰色にしない。理由を必ず出す。 */}
              {editable && requestBlockedBy !== null && (
                <p className="resource-note">
                  承認を依頼できません: {requestBlockedBy}
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}

function PlaylistOptions({
  playlistId,
  name,
}: {
  playlistId: string;
  name: string;
}) {
  const detail = useResource(
    (signal) => api.getPlaylist(playlistId, signal),
    [playlistId],
  );
  if (detail.state !== "ready") return null;
  return (
    <>
      {detail.value.revisions.map((revision: PlaylistRevision) => (
        <option key={revision.id} value={revision.id}>
          {name} — Revision {revision.revisionNumber}（{revision.state}）
        </option>
      ))}
    </>
  );
}

function SectionHeading() {
  return (
    <header className="section-heading">
      <div>
        <h2>スケジュール</h2>
        <p>どのLEDへ、いつ、どのプレイリストを流すかを設定します。</p>
      </div>
    </header>
  );
}
